import { useEffect, useRef } from "react";
import { authService } from "../services/serviceFactory";
import { toast } from "@/hooks/use-toast";

export const useSessionMonitor = () => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckRef = useRef<number>(Date.now());

  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await authService.getAuthStatus();

        // If we were authenticated but now we're not, notify the user
        if (!response.isAuthenticated || !response.isAuthorized) {
          console.log("Session validation failed during periodic check");

          // Only show notification if it's been more than 30 seconds since last check
          // to avoid spam notifications during rapid checks
          if (Date.now() - lastCheckRef.current > 30000) {
            toast({
              title: "Session Expired",
              description:
                "Your session has expired. Please sign in again to continue making changes.",
              variant: "destructive",
              duration: 7000,
            });
          }

          // Clear the interval since we'll be redirecting
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }

          // Store current path and redirect
          const currentPath = window.location.pathname + window.location.search;
          if (currentPath !== "/" && currentPath !== "/signin") {
            localStorage.setItem("redirectAfterAuth", currentPath);
          }

          setTimeout(() => {
            window.location.href = "/signin";
          }, 3000); // Give user time to read the notification
        }

        lastCheckRef.current = Date.now();
      } catch (error) {
        console.log("Session check failed:", error);
        // Don't show notifications for failed session checks to avoid noise
      }
    };

    // Check session every 5 minutes
    intervalRef.current = setInterval(checkSession, 5 * 60 * 1000);

    // Also check when the page becomes visible (user returns to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkSession();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
};
